import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { uniqBy } from 'lodash';
import { STRINGS } from '../../constants';
import { AddressHelpers } from '../../helpers';
import BillingAddressLayout from './BillingAddressLayout';

const AddressSelector = ({
  billingCollectionAddresses,
  selectedAddressId,
  setSelectedAddressId,
}) => (
  <FormControl variant='outlined' fullWidth>
    <InputLabel id='billing-address-label'>
      {selectedAddressId
        ? STRINGS.BILLING_ADDRESS
        : STRINGS.SELECT_YOUR_BILLING_ADDRESS}
    </InputLabel>
    <Select
      name='billing-address'
      data-testid='billing-address-select'
      labelId='billing-address-label'
      onChange={event => setSelectedAddressId(event.target.value)}
      label={
        selectedAddressId
          ? STRINGS.BILLING_ADDRESS
          : STRINGS.SELECT_YOUR_BILLING_ADDRESS
      }
      value={selectedAddressId}
      variant='outlined'
      fullWidth
      sx={{
        '& .MuiSelect-select': {
          '& .MuiTypography-root': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
      }}
      MenuProps={{
        PaperProps: {
          sx: { maxWidth: '30%' },
        },
      }}
      slotProps={{
        htmlInput: {
          id: 'billing-address-select-input',
          role: 'combobox',
          'aria-controls': `menu-billing-address`,
          'data-testid': 'billing-address-select-input',
        },
        input: {
          'data-testid': 'billing-address-select-input',
        },
      }}
      autoComplete='off'
    >
      {billingCollectionAddresses.map(({ address, id }) => (
        <MenuItem
          key={id}
          value={id}
          sx={{ whiteSpace: 'unset', wordBreak: 'break-all' }}
        >
          <Typography>{address}</Typography>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const BillingAddress = ({
  defaultBillingAddress,
  items,
  selectedAddressId,
  setSelectedAddressId,
}) => {
  const billingCollectionAddresses = uniqBy(
    AddressHelpers.getCollectionAddressDetails(items),
    'address'
  );
  const selectedAddressDetails = useMemo(
    () =>
      billingCollectionAddresses.find(
        address => address.id === selectedAddressId
      ),
    [billingCollectionAddresses, selectedAddressId]
  );

  useEffect(() => {
    if (billingCollectionAddresses.length === 1) {
      setSelectedAddressId(billingCollectionAddresses[0].id);
    }
    if (!selectedAddressDetails && billingCollectionAddresses.length !== 1) {
      setSelectedAddressId(null);
    }
  }, [billingCollectionAddresses, selectedAddressDetails]);

  return (
    <Paper sx={{ mt: 2 }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          padding: 2,
          flexWrap: 'nowrap',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h3'>{STRINGS.YOUR_BILLING_ADDRESS}</Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        {!defaultBillingAddress && billingCollectionAddresses.length > 1 && (
          <Box sx={{ pb: 2 }}>
            <AddressSelector
              billingCollectionAddresses={billingCollectionAddresses}
              selectedAddressId={selectedAddressId}
              setSelectedAddressId={setSelectedAddressId}
            />
          </Box>
        )}
        {defaultBillingAddress && (
          <BillingAddressLayout
            billingAddress={defaultBillingAddress}
            telephone={defaultBillingAddress.phoneNumber}
            email={defaultBillingAddress.email}
            contactName={defaultBillingAddress.contactName}
          />
        )}
        {billingCollectionAddresses.length !== 1 &&
          !defaultBillingAddress &&
          selectedAddressDetails && (
            <BillingAddressLayout
              billingAddress={selectedAddressDetails}
              telephone={selectedAddressDetails?.telephone}
              email={selectedAddressDetails?.email}
              contactName={selectedAddressDetails?.contactName}
            />
          )}
        {billingCollectionAddresses.length === 1 && !defaultBillingAddress && (
          <BillingAddressLayout
            billingAddress={billingCollectionAddresses[0]}
            telephone={billingCollectionAddresses[0]?.telephone}
            email={billingCollectionAddresses[0]?.email}
            contactName={billingCollectionAddresses[0]?.contactName}
          />
        )}
      </Box>
    </Paper>
  );
};

BillingAddress.propTypes = {
  defaultBillingAddress: PropTypes.object,
  items: PropTypes.array,
};

export default BillingAddress;
